import { Core } from "@faire/design-tokens";
import Color from "@faire/web--source/slate/Color";
import * as styles from "@faire/web--source/slate/Typography/styles.css";
import { TypographyVariants } from "@faire/web--source/slate/Typography/Types";
import { Props } from "@faire/web--source/slate/Typography/Typography";
import {
  FONT_RAMP,
  variantTagMap,
} from "@faire/web--source/slate/Typography/utils";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import cn from "classnames";
import React, { CSSProperties, ForwardedRef } from "react";

export const CustomTypography = React.forwardRef<
  HTMLElement,
  Omit<Props, "variant"> & {
    variant?: TypographyVariants;
    children?: React.ReactNode;
  }
>(
  (
    {
      as,
      variant = "paragraphSansRegular",
      align,
      color,
      truncate,
      maxLines,
      strikethrough,
      children,
      ...rest
    },
    ref
  ) => {
    const variantTag = getVariantTag(variant);
    const tag = as || variantTag || "span";

    color = resolveColor(color);
    const fontRamp = FONT_RAMP[variant] ?? FONT_RAMP["paragraphSansRegular"];
    const className = cn(
      styles.base,
      truncate && styles.truncate,
      maxLines && styles.maxLines,
      strikethrough && styles.strikethrough,
      styles.sprinkles({
        textAlign: align,
        ...fontRamp,
      }),
      color === "inherit" ? styles.inheritColor : styles.color,
      rest.className
    );

    const rampLineHeight = FONT_RAMP[variant]["line-height"];

    const style: CSSProperties = {
      ...assignInlineVars({
        [styles.colorVar]: color === "inherit" ? undefined : color,
        [styles.maxLinesVar]: maxLines ? String(maxLines) : "",
        [styles.maxHeightVar]:
          rampLineHeight && maxLines
            ? `calc(${maxLines} * ${rampLineHeight})`
            : undefined,
        /* 50% opacity - cannot use Opacity.0_50 constant as it is client side only */
        [styles.decorationColor]:
          color === "inherit" ? undefined : color + "80",
      }),
      ...rest.style,
    };

    return React.createElement(
      tag,
      {
        ...rest,
        style,
        className,
        ref: ref as ForwardedRef<HTMLElement | null>,
      },
      children
    );
  }
);

const getVariantTag = (variant: TypographyVariants) => variantTagMap[variant];

const resolveColor = (color: string | undefined) => {
  if (color && color in Color) {
    // @ts-expect-error this is a hack
    return Color[color as unknown as any];
  }

  if (!color) {
    return Core.text.default;
  }

  return color;
};
